import { Injectable, OnDestroy } from '@angular/core';
import Keycloak, { KeycloakTokenParsed } from 'keycloak-js';
import { BehaviorSubject, Subscription, interval } from 'rxjs';

import { environment } from '../environments/environment';
import { Bridge } from './bridge.service';

/**
 * Session-scoped record of the project the user chose to sign into.
 *
 * Login is a redirect round-trip through Keycloak, so on the way back the app has to
 * know which realm it was in the middle of joining without asking again. Session
 * storage is the right lifetime: it survives the redirect but not a new tab, so a
 * fresh visit always starts at the project picker rather than auto-joining a realm
 * that may since have been removed.
 */
const CONFIRMED_BRIDGE_KEY = 'plots-auth-bridge-id';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
    // Constructed when a project is chosen, not at module load: a realm baked in at
    // startup is what caused the auto-login into whatever was stored last.
    private keycloak: Keycloak | null = null;
    private authenticated = false;
    private userDetails = new BehaviorSubject<KeycloakTokenParsed | null>(null);
    private refreshSubscription?: Subscription;
    private tokenChanged = new BehaviorSubject<string | undefined>(undefined);
    private refreshFailures = 0;
    private static readonly MAX_REFRESH_FAILURES = 3;

    public token$ = this.tokenChanged.asObservable();
    public userDetails$ = this.userDetails.asObservable();

    /** The project the user confirmed for this browser session, if any. */
    get confirmedBridgeId(): string | null {
        try {
            return sessionStorage.getItem(CONFIRMED_BRIDGE_KEY);
        } catch {
            return null;
        }
    }

    confirmBridge(id: string): void {
        try {
            sessionStorage.setItem(CONFIRMED_BRIDGE_KEY, id);
        } catch {
            // Without storage the redirect back cannot be recognised; login still works
            // for this page load, the picker will just show again afterwards.
        }
    }

    clearConfirmedBridge(): void {
        try {
            sessionStorage.removeItem(CONFIRMED_BRIDGE_KEY);
        } catch {
            // nothing to clear
        }
    }

    /**
     * Sign into the given project's realm. Resolves true when authenticated; rejects
     * when Keycloak itself fails (realm gone, unreachable), so the caller can return
     * to the picker instead of leaving the user stuck on a Keycloak error page.
     */
    async init(bridge: Bridge): Promise<boolean> {
        this.keycloak = new Keycloak({
            url: environment.keycloakURL,
            realm: bridge.keycloakRealm,
            clientId: 'dashboard',
        });

        const authenticated = await this.keycloak.init({
            onLoad: 'login-required',
            checkLoginIframe: false,
        });
        this.authenticated = authenticated;
        if (authenticated) {
            this.userDetails.next(this.keycloak.tokenParsed ?? null);
            this.tokenChanged.next(this.keycloak.token);
            console.log(
                '[auth] signed in to realm',
                bridge.keycloakRealm,
                `access token valid for ${this.secondsToExpiry}s, refresh every 60s`,
            );
            this.refreshSubscription = interval(60000).subscribe(() => this.updateToken());
        }
        return authenticated;
    }

    isLoggedIn(): boolean {
        return this.authenticated;
    }

    getToken(): string | undefined {
        return this.keycloak?.token;
    }

    get secondsToExpiry(): number | null {
        const exp = this.keycloak?.tokenParsed?.exp;
        if (!exp) {
            return null;
        }
        return Math.round(exp + (this.keycloak?.timeSkew ?? 0) - Date.now() / 1000);
    }

    get username(): string {
        const parsed = this.keycloak?.tokenParsed as
            | { preferred_username?: string; name?: string }
            | undefined;
        return parsed?.preferred_username || parsed?.name || '';
    }

    updateToken(): Promise<boolean> {
        const keycloak = this.keycloak;
        if (!keycloak) {
            return Promise.resolve(false);
        }
        return keycloak
            .updateToken(70)
            .then((refreshed) => {
                this.refreshFailures = 0;
                if (refreshed) {
                    this.userDetails.next(keycloak.tokenParsed ?? null);
                    this.tokenChanged.next(keycloak.token);
                }
                return refreshed;
            })
            .catch((err) => {
                this.refreshFailures += 1;
                console.error(
                    `Token refresh failed (${this.refreshFailures}/${AuthService.MAX_REFRESH_FAILURES})`,
                    err,
                );
                if (this.refreshFailures < AuthService.MAX_REFRESH_FAILURES) {
                    return false;
                }
                // Re-authenticate by redirect rather than signing out: the SSO cookie
                // is usually still valid, so this returns with a fresh token pair. The
                // confirmed project is kept so the return trip skips the picker.
                console.warn('[auth] re-authenticating via redirect after repeated refresh failures');
                this.refreshFailures = 0;
                keycloak.login({ redirectUri: window.location.href });
                return false;
            });
    }

    login(): void {
        this.keycloak?.login();
    }

    /** Sign out and forget the chosen project, so the next visit starts at the picker. */
    logout(): void {
        this.clearConfirmedBridge();
        this.keycloak?.logout({ redirectUri: window.location.origin });
    }

    ngOnDestroy(): void {
        this.refreshSubscription?.unsubscribe();
    }
}
